import React, { useState, useEffect } from 'react';
import { ArrowLeft, BarChart3, Settings, Plus, Minus, DollarSign, CheckCircle, Users, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useResponsive } from '../../hooks/responsive/useResponsive';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import TransactionHistoryModal from './shared/TransactionHistoryModal';
import AllowanceSettingsModal from './shared/AllowanceSettingsModal';
import KidsAllowanceView from './KidsAllowanceView';

export default function IncomePage() {
  const { currentUser, family, kidAllowances, addAllowanceTransaction, allowanceTransactions } = useFamilyContext();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  
  // State for quick transaction
  const [selectedKid, setSelectedKid] = useState(null);
  const [selectedAction, setSelectedAction] = useState('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [transactionSuccess, setTransactionSuccess] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [historyKidId, setHistoryKidId] = useState(null);
  const [showKidsOverview, setShowKidsOverview] = useState(false);
  const [showAllowanceSettings, setShowAllowanceSettings] = useState(false);
  const [showKidsView, setShowKidsView] = useState(false);
  
  const isParent = currentUser?.isParent;

  // Clear success message after 3 seconds
  useEffect(() => {
    if (transactionSuccess) {
      const timer = setTimeout(() => setTransactionSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [transactionSuccess]);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Calculate family totals
  const getFamilyTotals = () => {
    if (!family?.kids) return { totalBalance: 0, totalWeekly: 0 };
    
    let totalBalance = 0;
    let totalWeekly = 0;
    
    family.kids.forEach(kid => {
      const allowance = kidAllowances[kid.id];
      if (allowance) {
        totalBalance += allowance.current_balance || 0;
        totalWeekly += allowance.weekly_amount || 0;
      }
    });
    
    return { totalBalance, totalWeekly };
  };

  const { totalBalance, totalWeekly } = getFamilyTotals();

  // Get recent transactions for activity feed
  const getRecentTransactions = () => {
    if (!allowanceTransactions || !family?.kids) return [];
    
    // allowanceTransactions is an object where keys are kidIds and values are arrays of transactions
    const allTransactions = [];
    
    // Flatten all transactions from all kids
    Object.entries(allowanceTransactions).forEach(([kidId, transactions]) => {
      if (Array.isArray(transactions)) {
        transactions.forEach(transaction => {
          const kid = family.kids.find(k => k.id === kidId);
          if (transaction && transaction.amount != null && transaction.created_at) {
            allTransactions.push({
              id: transaction.id || Math.random(),
              type: transaction.type || (transaction.amount > 0 ? 'addition' : 'deduction'),
              amount: Math.abs(transaction.amount || 0),
              reason: transaction.reason || transaction.description || 'Transaction',
              created_at: transaction.created_at,
              kidName: kid?.name || 'Unknown Kid',
              kidId: kidId
            });
          }
        });
      }
    });
    
    // Sort by newest first and take top 5
    return allTransactions
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  };

  // Handle quick transaction
  const handleQuickTransaction = async () => {
    if (!selectedKid || !amount || !selectedAction) return;
    
    const numAmount = parseFloat(amount);
    if (numAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setIsProcessing(true);
    try {
      const transactionReason = reason.trim() || (selectedAction === 'add' ? 'Addition' : 'Deduction');
      if (selectedAction === 'add') {
        await addAllowanceTransaction(selectedKid.id, numAmount, 'addition', transactionReason);
        setTransactionSuccess({
          type: 'add',
          amount: numAmount,
          kidName: selectedKid.name
        });
      } else {
        await addAllowanceTransaction(selectedKid.id, numAmount, 'deduction', transactionReason);
        setTransactionSuccess({
          type: 'remove',
          amount: numAmount,
          kidName: selectedKid.name
        });
      }
      
      // Reset form
      setAmount('');
      setReason('');
      setSelectedKid(null);
    } catch (error) {
      console.error('Transaction error:', error);
      alert('Transaction failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Get kid avatar color
  const getKidAvatarColor = (kid, index) => {
    const colors = [
      'bg-gradient-to-br from-pink-400 to-pink-600',
      'bg-gradient-to-br from-azure-400 to-indigo-600', 
      'bg-gradient-to-br from-violet-400 to-pink-500',
      'bg-gradient-to-br from-yellow-400 to-orange-500'
    ];
    return colors[index % colors.length];
  };

  const closeModal = () => setHistoryKidId(null);

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 30) return `${diffInDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Kid view (for kids or when parent chooses to see kids view)
  if (!isParent || showKidsView) {
    return <KidsAllowanceView />;
  }

  const recentTransactions = getRecentTransactions();

  return (
    <>
      <TransactionHistoryModal
        isOpen={!!historyKidId}
        onClose={closeModal}
        kidId={historyKidId}
      />
      
      <AllowanceSettingsModal
        isOpen={showAllowanceSettings}
        onClose={() => setShowAllowanceSettings(false)}
      />
      
      <div className="min-h-screen bg-stone-50">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-stone-600" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-6 bg-teal-500 rounded-full"></div>
                  <h1 className={`font-bold text-slate-900 ${isMobile ? 'text-lg' : 'text-lg'}`}>
                    Income Hub
                  </h1>
                </div>
              </div>
              <div className="flex gap-2">
                {isMobile && (
                  <button 
                    onClick={() => setShowKidsView(true)}
                    className="flex items-center gap-1 px-3 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors text-sm"
                  >
                    <Users className="w-4 h-4" />
                    Kids
                  </button>
                )}
              {!isMobile && (
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowKidsView(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                  >
                    <Users className="w-4 h-4" />
                    Kids View
                  </button>
                  <button 
                    onClick={() => setShowKidsOverview(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Reports
                  </button>
                 
                </div>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {transactionSuccess && (
          <div className="max-w-[1200px] mx-auto px-4 pb-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3 shadow-sm">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span className="text-emerald-800 font-medium">
                {transactionSuccess.type === 'add' 
                  ? `✅ Added ${formatMoney(transactionSuccess.amount)} to ${transactionSuccess.kidName}'s allowance`
                  : `✅ Removed ${formatMoney(transactionSuccess.amount)} from ${transactionSuccess.kidName}'s allowance`
                }
              </span>
            </div>
          </div>
        )}

        {/* Kids Overview Modal */}
        {showKidsOverview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-auto">
              <div className="sticky top-0 bg-white border-b border-stone-200 p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Users className="w-6 h-6" />
                    Kids' Allowance Overview
                  </h2>
                  <button
                    onClick={() => setShowKidsOverview(false)}
                    className="text-stone-400 hover:text-stone-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {family?.kids?.map((kid, index) => {
                  const allowance = kidAllowances[kid.id] || {};
                  return (
                    <div key={kid.id} className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <AvatarOrInitial
                            avatar={kid.avatar}
                            name={kid.name}
                            size="md"
                            className={getKidAvatarColor(kid, index)}
                          />
                          <div>
                            <div className="font-semibold text-slate-900">{kid.name}</div>
                            <div className="text-sm text-stone-500">
                              Weekly: {formatMoney(allowance.weekly_amount || 0)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-slate-900">
                            {formatMoney(allowance.current_balance || 0)}
                          </div>
                          <button
                            onClick={() => {
                              setHistoryKidId(kid.id);
                              setShowKidsOverview(false);
                            }}
                            className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                          >
                            <History className="w-3 h-3" />
                            View History
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Main Container */}
        <div className={`max-w-[1200px] mx-auto p-4 ${isMobile ? 'block space-y-4' : 'grid grid-cols-[2fr_1fr] gap-6'}`}>
          {/* Main Content */}
          <div className="space-y-4">
            {/* Balance Overview - Matching mockup style */}
            <div className="relative bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 rounded-xl p-6 text-white overflow-hidden">
              <div className="absolute top-[-50%] right-[-20%] w-32 h-32 bg-white/5 rounded-full transform rotate-45"></div>
              <div className="relative z-10">
                <div className="text-sm opacity-80 mb-1">Family Allowance Balance</div>
                <div className="text-3xl font-bold mb-1">{formatMoney(totalBalance)}</div>
                <div className="text-xs opacity-70">Total owed to kids</div>
              </div>
            </div>

            {/* Quick Transaction */}
            <div className="bg-white rounded-xl p-5 border border-stone-200 shadow-sm">
              <div className="text-center mb-5">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900 mb-1">Quick Transaction</h2>
              </div>

              {/* Select Kid */}
              <div className="mb-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {family?.kids?.map((kid, index) => (
                    <div
                      key={kid.id}
                      onClick={() => setSelectedKid(kid)}
                      className={`flex flex-col items-center p-3 rounded-lg cursor-pointer transition-all border-2 ${
                        selectedKid?.id === kid.id
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-transparent bg-stone-50 hover:bg-stone-100'
                      }`}
                    >
                      <AvatarOrInitial
                        avatar={kid.avatar}
                        name={kid.name}
                        size="md"
                        className={`mb-1 ${getKidAvatarColor(kid, index)}`}
                      />
                      <span className="text-xs font-medium text-slate-900">{kid.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Selection */}
              <div className="mb-6">
                <h3 className="text-center text-base font-semibold text-slate-900 mb-3">Action</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    onClick={() => setSelectedAction('add')}
                    className={`p-3 rounded-lg cursor-pointer transition-all border-2 text-center ${
                      selectedAction === 'add'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                  >
                    <Plus className={`w-5 h-5 mx-auto mb-1 ${selectedAction === 'add' ? 'text-teal-600' : 'text-stone-600'}`} />
                    <div className="font-semibold text-slate-900 mb-1 text-sm">Add Money</div>
                    <div className="text-xs text-stone-500">Allowance, chores, rewards</div>
                  </div>
                  <div
                    onClick={() => setSelectedAction('remove')}
                    className={`p-3 rounded-lg cursor-pointer transition-all border-2 text-center ${
                      selectedAction === 'remove'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                  >
                    <Minus className={`w-5 h-5 mx-auto mb-1 ${selectedAction === 'remove' ? 'text-teal-600' : 'text-stone-600'}`} />
                    <div className="font-semibold text-slate-900 mb-1 text-sm">Remove Money</div>
                    <div className="text-xs text-stone-500">Purchases, penalties</div>
                  </div>
                </div>
              </div>

              {/* Amount Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Amount</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-stone-500 text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Reason Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Reason (optional)</label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder={selectedAction === 'add' ? 'e.g., Chores completed, bonus for grades' : 'e.g., Bought toy, broke something'}
                  maxLength={100}
                />
                <div className="text-xs text-stone-500 mt-1">
                  {reason.length}/100 characters
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleQuickTransaction}
                disabled={isProcessing || !selectedKid || !amount || !selectedAction}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  selectedAction === 'add'
                    ? 'bg-teal-600 hover:bg-teal-700 text-white disabled:bg-stone-300'
                    : 'bg-coral-600 hover:bg-coral-700 text-white disabled:bg-stone-300'
                } disabled:text-stone-500 disabled:cursor-not-allowed`}
              >
                {isProcessing ? 'Processing...' : `${selectedAction === 'add' ? 'Add' : 'Remove'} ${formatMoney(parseFloat(amount) || 0)}`}
              </button>
            </div>

            {/* Family Jobs */}
            <div className="bg-white rounded-xl p-5 border border-stone-200 border-l-4 border-l-teal-500">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-lg">🏆</span>
                <h3 className="text-lg font-semibold text-slate-900">Family Jobs</h3>
              </div>
              
              <div className="text-center py-8">
                <div className="text-3xl mb-3 opacity-50">🎯</div>
                <h4 className="text-base font-semibold text-slate-900 mb-1">No jobs created yet</h4>
                <p className="text-stone-500 text-sm mb-4">Create jobs for kids to earn extra allowance</p>
                <button 
                  onClick={() => navigate('/jobs')}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  Create First Job
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Recent Activity */}
            <div className="bg-white rounded-xl p-4 border border-stone-200">
              <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                ⚡ Recent Activity
              </h3>
              {recentTransactions.length === 0 ? (
                <div className="text-center text-stone-500 text-xs py-4">
                  No recent activity
                </div>
              ) : (
                <div className="space-y-2">
                  {recentTransactions.map(transaction => (
                    <div key={transaction.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-b-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${transaction.type === 'addition' ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{transaction.kidName}</div>
                          <div className="text-xs text-stone-500">{transaction.reason}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-semibold ${transaction.type === 'addition' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {transaction.type === 'addition' ? '+' : '-'}{formatMoney(transaction.amount)}
                        </div>
                        <div className="text-xs text-stone-400">{formatTimeAgo(transaction.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Allowance Settings */}
            <div className="bg-white rounded-xl p-4 border border-stone-200">
              <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                ⚙️ Allowance Settings
              </h3>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">Weekly Allowance</div>
                    <div className="text-xs text-stone-500">Automatic distribution</div>
                  </div>
                  <div className="font-semibold text-slate-900 text-sm">{formatMoney(totalWeekly)}</div>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">Distribution Day</div>
                    <div className="text-xs text-stone-500">When allowance is given</div>
                  </div>
                  <div className="font-semibold text-slate-900 text-sm">Sunday</div>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">Job Notifications</div>
                    <div className="text-xs text-stone-500">Remind kids about jobs</div>
                  </div>
                  <div className="font-semibold text-slate-900 text-sm">On</div>
                </div>
              </div>
              
              <button 
                onClick={() => setShowAllowanceSettings(true)}
                className="w-full mt-3 bg-slate-900 hover:bg-slate-950 text-white py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Manage Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
